/**
 * Exposure Analytics — Phase 4 of the APC Engine.
 *
 * Dashboard mostrando impresiones, clicks y CTR por producto / promoción /
 * combo. Sustenta la rotación inteligente: identifica qué se muestra
 * mucho pero no convierte y qué casi nadie ha visto.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { ChartPieSlice, Eye, CursorClick, TrendUp } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const PERIODS = [
  { v: "1d", label: "Hoy" },
  { v: "7d", label: "7 días" },
  { v: "30d", label: "30 días" },
  { v: "all", label: "Todo" },
];

const KINDS = [
  { v: "", label: "Todo" },
  { v: "product", label: "Productos" },
  { v: "promotion", label: "Promociones" },
  { v: "combo", label: "Combos" },
];

const KIND_TAG = {
  product: { label: "Producto", color: "bg-zinc-100 text-zinc-700" },
  promotion: { label: "Promo", color: "bg-red-50 text-red-700" },
  combo: { label: "Combo", color: "bg-purple-50 text-purple-700" },
};

export default function PromotionsAnalytics() {
  const [period, setPeriod] = useState("7d");
  const [kind, setKind] = useState("");
  const [data, setData] = useState({ totals: {}, rows: [] });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ period });
      if (kind) params.set("kind", kind);
      const { data: d } = await api.get(`/exposure/stats?${params.toString()}`);
      setData(d);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error cargando analítica");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period, kind]);

  const totals = data.totals || {};
  const rows = data.rows || [];

  return (
    <div className="p-6 sm:p-8 space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-data text-xs tracking-widest text-zinc-500 mb-1">MOTOR COMERCIAL · FASE 4</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight inline-flex items-center gap-3">
            <ChartPieSlice size={32} weight="duotone" className="text-[#7E57C2]" />
            Analítica de exposición
          </h1>
          <p className="text-zinc-500 mt-1 text-sm">Impresiones, clicks y CTR por elemento promocional · usado para la rotación inteligente.</p>
        </div>
        <Button onClick={load} variant="outline" disabled={loading} data-testid="analytics-refresh">
          {loading ? "Cargando…" : "Refrescar"}
        </Button>
      </header>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 bg-white border border-zinc-200 rounded p-3">
        <div className="flex items-center gap-1">
          <span className="text-xs text-zinc-500 uppercase tracking-wider mr-1">Periodo:</span>
          {PERIODS.map((p) => (
            <button key={p.v} onClick={() => setPeriod(p.v)}
              className={`text-xs px-3 py-1.5 rounded-full ${period === p.v ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"}`}
              data-testid={`analytics-period-${p.v}`}>{p.label}</button>
          ))}
        </div>
        <div className="h-5 w-px bg-zinc-200 mx-1" />
        <div className="flex items-center gap-1">
          <span className="text-xs text-zinc-500 uppercase tracking-wider mr-1">Tipo:</span>
          {KINDS.map((k) => (
            <button key={k.v || "all"} onClick={() => setKind(k.v)}
              className={`text-xs px-3 py-1.5 rounded-full ${kind === k.v ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"}`}
              data-testid={`analytics-kind-${k.v || "all"}`}>{k.label}</button>
          ))}
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="analytics-totals">
        <SummaryCard icon={Eye} color="#002FA7" label="Impresiones" value={totals.impressions || 0} />
        <SummaryCard icon={CursorClick} color="#E0000F" label="Clicks" value={totals.clicks || 0} />
        <SummaryCard icon={TrendUp} color="#00873D" label="CTR" value={`${totals.ctr || 0}%`} />
        <SummaryCard icon={ChartPieSlice} color="#7E57C2" label="Elementos rastreados" value={totals.tracked_items || 0} />
      </div>

      {/* Table */}
      <div className="bg-white border border-zinc-200 rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
            <tr>
              <th className="text-left px-4 py-3">Elemento</th>
              <th className="text-left px-4 py-3">Tipo</th>
              <th className="text-right px-4 py-3">Impresiones</th>
              <th className="text-right px-4 py-3">Clicks</th>
              <th className="text-right px-4 py-3">CTR</th>
              <th className="text-left px-4 py-3">Último visto</th>
            </tr>
          </thead>
          <tbody data-testid="analytics-table-body">
            {loading && (
              <tr><td colSpan={6} className="px-4 py-16 text-center text-zinc-400">Cargando…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-16 text-center text-zinc-400">Sin datos en este periodo. Abre la tienda para empezar a recolectar.</td></tr>
            )}
            {!loading && rows.map((r) => {
              const tag = KIND_TAG[r.kind] || KIND_TAG.product;
              return (
                <tr key={`${r.kind}-${r.ref_id}`} className="border-t border-zinc-100 hover:bg-zinc-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {r.photo ? (
                        <img src={r.photo} alt="" className="w-9 h-9 rounded object-cover border border-zinc-200" />
                      ) : (
                        <div className="w-9 h-9 rounded bg-zinc-100 flex items-center justify-center text-zinc-400 text-[10px]">–</div>
                      )}
                      <div>
                        <div className="font-semibold leading-tight">{r.name}</div>
                        {r.brand && <div className="text-[10px] uppercase text-zinc-400 font-mono">{r.brand}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-semibold ${tag.color}`}>{tag.label}</span>
                  </td>
                  <td className="px-4 py-3 text-right numeric font-semibold">{r.impressions.toLocaleString("es-MX")}</td>
                  <td className="px-4 py-3 text-right numeric">{r.clicks.toLocaleString("es-MX")}</td>
                  <td className="px-4 py-3 text-right numeric">
                    <span className={r.ctr >= 10 ? "text-emerald-700 font-semibold" : r.ctr >= 3 ? "text-amber-700" : "text-zinc-500"}>
                      {r.ctr}%
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-500 font-mono">
                    {r.last_seen ? new Date(r.last_seen).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" }) : "–"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SummaryCard({ icon: Icon, color, label, value }) {
  return (
    <div className="bg-white border border-zinc-200 rounded p-4">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-zinc-500">{label}</span>
        <Icon size={20} weight="duotone" style={{ color }} />
      </div>
      <div className="mt-2 font-heading text-3xl font-extrabold tracking-tight" style={{ color }}>
        {typeof value === "number" ? value.toLocaleString("es-MX") : value}
      </div>
    </div>
  );
}
